import { getAuth, type User } from "firebase/auth";
import { firebaseApp } from "../firebase/app";
import { hasDashboardV2Access } from "./dashboard-v2";

// Derived from the public `key` in the extension's manifest.json — stable across
// installs. If that key ever changes, update this in lock-step.
const EXTENSION_ORIGIN = "chrome-extension://lcaooicmbkaalkapojkiglmmpcjbgolg";

// The extension's content script, running in a normal V1 browser tab, cannot make
// its request look like it came from chrome-extension://…, so it identifies
// itself with this marker instead. Granting it a reply gives nothing new away:
// anything already running on this origin has the Firebase session anyway.
const EXTENSION_REQUEST_MARKER = "drivee-extension";

// The extension loads every dashboard URL in its panel with `?client=extension`.
// react-router drops the query on internal navigation, so the marker is captured
// once at init into sessionStorage (scoped to the tab / the panel's partition).
const EXTENSION_CLIENT_KEY = "driveeExtensionClient";

let initialized = false;

// Cached because the extension asks every 30s and the answer is an RTDB read
// that cannot change within a session.
let dashboardTarget: "v1" | "v2" | undefined;

const captureExtensionClient = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("client") === "extension") sessionStorage.setItem(EXTENSION_CLIENT_KEY, "true");
  } catch (_e) {
    // Private mode / storage disabled — fall through to "not the extension".
  }
};

/** True when this page is running inside the Chrome extension's side panel. */
export const isExtensionClient = (): boolean => {
  try {
    return sessionStorage.getItem(EXTENSION_CLIENT_KEY) === "true";
  } catch (_e) {
    return false;
  }
};

// The flag lives on the dealership (manager) account: staff, supervisors,
// finance and BDC users must be checked against their manager's uid, never
// their own. `hasDashboardV2Access` already returns false for `useBothVersions`
// dealerships, which is exactly right here — they keep using V1 in the panel.
const resolveDashboardTarget = async (user: User) => {
  if (dashboardTarget) return dashboardTarget;
  const result = await user.getIdTokenResult();
  const dealerUid = (result?.claims?.managerUID as string) ?? user.uid;
  dashboardTarget = (await hasDashboardV2Access(dealerUid)) ? "v2" : "v1";
  return dashboardTarget;
};

const buildPayload = async (user: User | null) => {
  if (!user) {
    // Drop the cache here, not on a sign-out event: with no subscription this
    // is where we learn the user is gone, and a stale target would otherwise be
    // reported for whoever signs in next on this tab.
    dashboardTarget = undefined;
    // `dashboard` is deliberately omitted while signed out: we have nothing to
    // resolve it from, and the extension treats "absent" as "unchanged".
    return { token: null, uid: null, email: null };
  }
  return {
    // Firebase hands back an already-refreshed token when the current one is
    // within 5 minutes of expiry, so every reply carries a valid token.
    token: await user.getIdToken(),
    uid: user.uid,
    email: user.email,
    // Best-effort: a failed flag read must still yield a usable auth reply.
    // Absent means "don't know yet", never "v1".
    dashboard: await resolveDashboardTarget(user).catch(() => undefined),
  };
};

/**
 * Enables the Chrome extension auth handoff. Must be *called* (not merely
 * imported) from index.tsx: package.json marks non-CSS modules side-effect-free,
 * so a bare `import "./utils/extension-bridge"` can be tree-shaken out of the
 * production bundle. Keep this out of the React tree so a failure here can never
 * block the dashboard's own auth bootstrap.
 *
 * Replies only. Never posts to the extension unprompted — see the protocol note
 * in extra/salesguru-frontend-app-v1.md. Posting first meant naming a
 * chrome-extension:// targetOrigin of our own accord, which threw in mobile
 * WebKit and left PrivateRoute stuck on the skeleton screen. The extension polls
 * every 30s (and on refocus), which is what now carries the hourly ID-token
 * refresh and sign-out detection.
 */
export const initExtensionBridge = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  captureExtensionClient();
  const auth = getAuth(firebaseApp);

  window.addEventListener("message", (event: MessageEvent) => {
    // Plain string comparison — no URL parsing, safe in every engine.
    const fromPanelFrame = event.origin === EXTENSION_ORIGIN;
    const fromExtensionTab =
      event.origin === window.location.origin && event.data?.from === EXTENSION_REQUEST_MARKER;

    if (!fromPanelFrame && !fromExtensionTab) return;
    if (event.data?.type !== "REQUEST_AUTH_STATE" || !event.source) return;

    // Captured per event, never stored: replying to the sender is what keeps us
    // from ever naming a chrome-extension:// origin we weren't handed.
    const win = event.source as Window;
    const origin = event.origin;

    // currentUser is null until Firebase restores the persisted session.
    auth
      .authStateReady()
      .then(async () => {
        const payload = await buildPayload(auth.currentUser);
        win.postMessage({ type: "FIREBASE_AUTH_STATE", payload }, origin);
      })
      // Never rethrow: nothing in the dashboard's auth flow may depend on this.
      .catch((error) => console.warn("extension auth bridge failed", error));
  });
};
